import { useCallback, useState } from 'react';
import { apiJson } from '../api/client';
import { mutateGallery } from './galleryCache';

export interface UseGallerySelectionResult {
  selectMode: boolean;
  selected: Set<string>;
  toggleSelectMode: () => void;
  toggleSelected: (id: string) => void;
  clearSelected: () => void;
  selectAll: (ids: string[]) => void;
  /** Returns true when every deletion succeeded. Errors are aggregated. */
  deleteSelected: () => Promise<boolean>;
  deleting: boolean;
  error: string | null;
}

/**
 * Stage 12 (D1): reusable multi-select + bulk-delete for any gallery.
 * Lives outside the gallery components so three identical widgets don't
 * drift. Relies on A5's `mutateGallery` for a single atomic cache update
 * after all delete network calls complete.
 *
 * `endpoint` must be the same string passed to `useGallery()` — shared
 * cache key.
 */
export function useGallerySelection(
  endpoint: string,
): UseGallerySelectionResult {
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggleSelectMode = useCallback(() => {
    setSelectMode((prev) => {
      if (prev) setSelected(new Set());
      return !prev;
    });
    setError(null);
  }, []);

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelected = useCallback(() => {
    setSelected(new Set());
  }, []);

  const selectAll = useCallback((ids: string[]) => {
    setSelected(new Set(ids));
  }, []);

  const deleteSelected = useCallback(async (): Promise<boolean> => {
    if (selected.size === 0) return true;
    setDeleting(true);
    setError(null);
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((id) =>
        apiJson('DELETE', `${endpoint}/${encodeURIComponent(id)}`),
      ),
    );
    const failed = results
      .map((r, i) => ({ r, id: ids[i] }))
      .filter((x) => x.r.status === 'rejected');
    const deletedIds = new Set(
      results
        .map((r, i) => ({ r, id: ids[i] }))
        .filter((x) => x.r.status === 'fulfilled')
        .map((x) => x.id),
    );
    // One atomic cache mutation for every successful delete.
    mutateGallery(endpoint, (arr) => arr.filter((it) => !deletedIds.has(it.id)));
    setSelected(new Set());
    setSelectMode(false);
    setDeleting(false);
    if (failed.length > 0) {
      setError(
        `${failed.length} of ${ids.length} deletes failed. First error: ${
          (failed[0].r as PromiseRejectedResult).reason
        }`,
      );
      return false;
    }
    return true;
  }, [endpoint, selected]);

  return {
    selectMode,
    selected,
    toggleSelectMode,
    toggleSelected,
    clearSelected,
    selectAll,
    deleteSelected,
    deleting,
    error,
  };
}
